//#region IMPORT
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddRounded as AddIcon,
  SaveRounded as SaveIcon,
  DeleteOutlineRounded as DeleteIcon,
  RefreshRounded as RefreshIcon,
  CategoryOutlined as CategoryIcon,
} from '@mui/icons-material';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
//#endregion

/**
 * 유해위험물질(한라) 구분 목록 패널
 * - 사업장별 구분(TB_BO_HAM_HAN_GUBUN)을 등록/수정/삭제
 * - 항상 하나의 구분이 선택된 상태 (구분이 있으면 첫 번째 자동 선택)
 * - 구분을 클릭하면 onSelect(gubunId)로 부모에 전달 (구분이 없으면 0)
 */
function HAM_HAN_GubunPanel({ selectedGubunId, onSelect, validateSelection }) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const { custCd } = useSelector((state) => state.SearchInfo);

  const [rows, setRows] = useState([]);
  const [tempSeq, setTempSeq] = useState(0);
  // 화면에서 강조/삭제 대상으로 잡은 구분 (신규 미저장 행은 음수 ID)
  const [activeId, setActiveId] = useState(selectedGubunId || 0);
  const mountedRef = useRef(false);

  const notify = useCallback(
    (message, type = 'warning') => dispatch(showSnackbar({ message, type })),
    [dispatch]
  );

  // 응답에서 실패 사유 추출
  const getFailMessage = (res) => {
    const list = Array.isArray(res?.data) ? res.data : [];
    const fail = list.find((r) => r && r.isSuccess === false);
    return fail?.message || res?.message || '처리에 실패했습니다.';
  };

  // 활성 구분 결정: 유지할 수 있으면 유지, 아니면 첫 번째, 없으면 0
  const applySelection = useCallback(
    (list, preferredId) => {
      const exists = list.some((r) => r.gubunId === preferredId);
      const nextId = exists ? preferredId : (list[0]?.gubunId ?? 0);
      setActiveId(nextId);
      onSelect?.(nextId);
    },
    [onSelect]
  );

  const search = useCallback(
    async (preferredId) => {
      if (!custCd) {
        setRows([]);
        setActiveId(0);
        onSelect?.(0);
        return [];
      }
      try {
        const res = await apiRequest(
          'post',
          Global.urls.HazardousMaterials.getHamHanGubunR1,
          { CustCd: Commons.EncryptAES(custCd) }
        );
        const list = (Array.isArray(res?.data) ? res.data : []).map((r) => ({
          gubunId: r.gubunId,
          gubunNm: r.gubunNm ?? '',
          sortOrder: r.sortOrder ?? 0,
          editYn: 'N',
        }));
        setRows(list);
        applySelection(list, preferredId);
        return list;
      } catch (e) {
        notify(e?.message || '구분 조회 중 오류가 발생했습니다.', 'error');
        return [];
      }
    },
    [custCd, notify, applySelection, onSelect]
  );

  // 탭 전환으로 다시 마운트돼도 선택 구분을 유지하고,
  // 사업장이 실제로 바뀐 경우에만 선택을 초기화
  useEffect(() => {
    const preferred = mountedRef.current ? 0 : selectedGubunId || 0;
    mountedRef.current = true;
    search(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 사업장 변경 시에만 실행
  }, [custCd]);

  const handleAdd = () => {
    if (!validateSelection?.()) return;
    const nextTemp = tempSeq - 1;
    setTempSeq(nextTemp);
    const maxSort = rows.reduce((m, r) => Math.max(m, r.sortOrder || 0), 0);
    setRows((prev) => [
      ...prev,
      {
        gubunId: nextTemp, // 음수 = 신규(미저장)
        gubunNm: '',
        sortOrder: maxSort + 1,
        editYn: 'Y',
      },
    ]);
    setActiveId(nextTemp);
  };

  const handleChangeName = (gubunId, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.gubunId === gubunId ? { ...r, gubunNm: value, editYn: 'Y' } : r
      )
    );
  };

  const handleSave = async () => {
    if (!validateSelection?.()) return;
    const targets = rows.filter((r) => r.editYn === 'Y');
    if (targets.length === 0) {
      notify('저장할 구분이 없습니다.');
      return;
    }
    if (targets.some((r) => !String(r.gubunNm || '').trim())) {
      notify('구분명을 입력하세요.');
      return;
    }

    const payload = targets.map((r) => ({
      GubunId: Commons.EncryptAES(String(r.gubunId > 0 ? r.gubunId : 0)),
      CustCd: Commons.EncryptAES(custCd),
      GubunNm: Commons.EncryptAES(String(r.gubunNm).trim()),
      SortOrder: Commons.EncryptAES(String(r.sortOrder || 0)),
      UserId: userInfo?.userid || '',
    }));

    try {
      const res = await apiRequest(
        'post1',
        Global.urls.HazardousMaterials.saveHamHanGubunU1,
        payload
      );
      if (res?.severity === 'error') {
        notify(getFailMessage(res), 'error');
        return;
      }
      notify('저장되었습니다.', 'success');
      // 신규 행을 저장한 경우 저장된 구분(이름 기준)을 선택
      const activeRow = rows.find((r) => r.gubunId === activeId);
      const list = await search(activeId > 0 ? activeId : 0);
      if (activeRow && activeId <= 0) {
        const saved = list.find(
          (r) => r.gubunNm === String(activeRow.gubunNm).trim()
        );
        if (saved) {
          setActiveId(saved.gubunId);
          onSelect?.(saved.gubunId);
        }
      }
    } catch (e) {
      notify(e?.message || '구분 저장 중 오류가 발생했습니다.', 'error');
    }
  };

  const handleDelete = async (target) => {
    if (!validateSelection?.()) return;
    if (!target) return;

    // 저장 전(신규) 행은 화면에서만 제거
    if (target.gubunId <= 0) {
      const remain = rows.filter((r) => r.gubunId !== target.gubunId);
      setRows(remain);
      applySelection(remain, selectedGubunId || 0);
      return;
    }

    if (!window.confirm(`구분 "${target.gubunNm}"을(를) 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const res = await apiRequest(
        'post1',
        Global.urls.HazardousMaterials.deleteHamHanGubunD1,
        [{ GubunId: Commons.EncryptAES(String(target.gubunId)) }]
      );
      if (res?.severity === 'error') {
        notify(getFailMessage(res), 'error');
        return;
      }
      notify('삭제되었습니다.', 'success');
      await search(0);
    } catch (e) {
      notify(e?.message || '구분 삭제 중 오류가 발생했습니다.', 'error');
    }
  };

  const handleClickRow = (r) => {
    setActiveId(r.gubunId);
    // 저장 전(신규) 구분은 조회 필터로 쓸 수 없으므로 부모 선택은 바꾸지 않음
    if (r.gubunId > 0) onSelect?.(r.gubunId);
  };

  const dirty = rows.some((r) => r.editYn === 'Y');

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        minHeight: 420, // 데이터가 없어도 화면이 줄어들지 않도록 최소 높이 유지
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: '0 2px 14px rgba(16, 24, 40, 0.07)',
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 2,
          pb: 1.25,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.05rem',
              lineHeight: 1.2,
              color: '#1f2937',
            }}
          >
            구분
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#9aa3af', mt: 0.25 }}>
            총 {rows.length}개
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="새로고침" arrow>
            <IconButton
              size="small"
              sx={{ color: '#9aa3af', '&:hover': { color: 'primary.main' } }}
              onClick={() => search(activeId > 0 ? activeId : 0)}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: '999px',
              px: 1.5,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            추가
          </Button>
        </Box>
      </Box>

      {/* 구분 목록: 항상 위에서 아래로(세로) 쌓임 */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          px: 1.25,
          pb: 1.25,
          gap: 0.5,
        }}
      >
        {rows.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.25,
              textAlign: 'center',
              px: 2,
            }}
          >
            <Box
              sx={(theme) => ({
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              })}
            >
              <CategoryIcon />
            </Box>
            <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
              등록된 구분이 없어요.
              <br />
              구분을 먼저 등록해 주세요.
            </Typography>
          </Box>
        )}

        {rows.map((r) => {
          const active = activeId === r.gubunId;
          return (
            <Box
              key={r.gubunId}
              onClick={() => handleClickRow(r)}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                width: '100%',
                boxSizing: 'border-box',
                flexShrink: 0,
                minHeight: 44,
                px: 1.5,
                cursor: 'pointer',
                borderRadius: '12px',
                backgroundColor: active
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                transition: 'background-color 0.15s',
                '&:hover': {
                  backgroundColor: active
                    ? alpha(theme.palette.primary.main, 0.14)
                    : '#f4f6f8',
                },
                '& .row-delete': { opacity: 0, transition: 'opacity 0.15s' },
                '&:hover .row-delete': { opacity: 1 },
              })}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  flexShrink: 0,
                  borderRadius: '50%',
                  backgroundColor: active ? 'primary.main' : '#d1d5db',
                }}
              />
              <TextField
                variant="standard"
                fullWidth
                size="small"
                placeholder="구분명 입력"
                value={r.gubunNm}
                onChange={(e) => handleChangeName(r.gubunId, e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '0.92rem',
                    fontWeight: active ? 700 : 500,
                    color: r.editYn === 'Y' ? '#e11d48' : '#1f2937',
                  },
                }}
                inputProps={{ maxLength: 100 }}
                autoFocus={r.gubunId <= 0}
              />
              <Tooltip title="삭제" arrow>
                <IconButton
                  className="row-delete"
                  size="small"
                  sx={{ color: '#9aa3af', '&:hover': { color: '#e11d48' } }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(r);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Box>

      {/* 변경사항이 있을 때만 나타나는 저장 영역 */}
      {dirty && (
        <Box sx={{ p: 1.25, pt: 0.5 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{
              borderRadius: '12px',
              fontWeight: 700,
              textTransform: 'none',
              py: 1,
            }}
          >
            변경사항 저장
          </Button>
        </Box>
      )}
    </Paper>
  );
}

export default HAM_HAN_GubunPanel;
